import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Api } from '../api/api';
import { Certificate, CertificateFile } from '../../models/interfaces/certificate.interface';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { getCrudErrors } from '../../utils/functions';
import { certificateMock } from '../../mocks/interfaces/certificate.mock';
import { applyMockCrudParams } from '../../utils/mock-crud.util';

const CERTIFICATES_LIMIT = 100;

@Injectable({
  providedIn: 'root',
})
export class CertificateService {

  //#region Private Properties

  private readonly api: Api = new Api();

  private certificates: Certificate[] = structuredClone(certificateMock);

  //#endregion

  //#region Real API

  public async getMine(params: CrudRequestParams<Certificate> = {}): Promise<Certificate[]> {
    if (environment.useFakeBackend) {
      return this.getMineMocked(params);
    }

    const url = createCrudUrl<Certificate>(environment.routes.certificates.me, {
      sort: [{ field: 'completedAt', order: 'DESC' }],
      limit: CERTIFICATES_LIMIT,
      ...params,
    });
    const { error, success } = await this.api.getAsync<PaginationDto<Certificate>>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success.data ?? [];
  }

  public async findByCourse(courseId: string): Promise<Certificate | undefined> {
    const [certificate] = await this.getMine({ search: { courseId: { $eq: courseId } }, limit: 1 });

    return certificate;
  }

  public async issue(courseId: string): Promise<Certificate> {
    if (environment.useFakeBackend) {
      return this.issueMocked(courseId);
    }

    const { error, success } = await this.api.postAsync<Certificate>(environment.routes.certificates.issue(courseId));

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  public async getFile(certificate: Certificate): Promise<CertificateFile> {
    if (environment.useFakeBackend) {
      return this.getFileMocked(certificate);
    }

    const { error, success } = await this.api.getBlobAsync(environment.routes.certificates.download(certificate.id));

    if (!success)
      throw new Error(await this.getBlobErrorMessage(error));

    return { blob: success, filename: this.buildFilename(certificate.courseTitle) };
  }

  public async download(certificate: Certificate): Promise<void> {
    const file = await this.getFile(certificate);

    this.saveFile(file);
  }

  //#endregion

  //#region Fake Backend

  public async getMineMocked(params: CrudRequestParams<Certificate> = {}): Promise<Certificate[]> {
    const active = this.certificates.filter(certificate => certificate.isActive);
    const sorted = applyMockCrudParams(active, {
      sort: [{ field: 'completedAt', order: 'DESC' }],
      ...params,
    });

    return params.limit ? sorted.slice(0, params.limit) : sorted;
  }

  public async issueMocked(courseId: string): Promise<Certificate> {
    const existing = this.certificates.find(certificate => certificate.courseId === courseId);

    if (existing) {
      return { ...existing };
    }

    const now = new Date();
    const certificate: Certificate = {
      id: crypto.randomUUID(),
      tenantId: certificateMock[0]?.tenantId ?? '',
      userId: certificateMock[0]?.userId ?? '',
      courseId,
      courseTitle: 'Curso concluído',
      courseDurationMinutes: null,
      completedAt: now,
      createdAt: now,
      updatedAt: now,
      isActive: true,
    };

    this.certificates = [certificate, ...this.certificates];

    return { ...certificate };
  }

  public async getFileMocked(certificate: Certificate): Promise<CertificateFile> {
    const blob = new Blob([`%PDF-1.4\n% Certificado ${certificate.courseTitle}\n%%EOF`], { type: 'application/pdf' });

    return { blob, filename: this.buildFilename(certificate.courseTitle) };
  }

  public resetMockData(): void {
    this.certificates = structuredClone(certificateMock);
  }

  //#endregion

  //#region Private Methods

  private saveFile(file: CertificateFile): void {
    const url = URL.createObjectURL(file.blob);
    const anchor = document.createElement('a');

    anchor.href = url;
    anchor.download = file.filename;
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(url);
  }

  private buildFilename(courseTitle: string): string {
    const slug = courseTitle
      .normalize('NFD')
      .replaceAll(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '-')
      .replaceAll(/(?:^-)|(?:-$)/g, '');

    return `certificado-${slug || 'curso'}.pdf`;
  }

  private async getBlobErrorMessage(error?: HttpErrorResponse): Promise<string> {
    if (error?.error instanceof Blob) {
      try {
        const body = JSON.parse(await error.error.text());

        return getCrudErrors({ error: body })[0];
      } catch {
        return 'Não foi possível baixar o certificado.';
      }
    }

    return getCrudErrors(error)[0];
  }

  //#endregion

}
